// import "./Anesthesisvisit.css";
import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Form, Input, Radio, Select, Alert } from "antd";
import { DateTime } from "luxon";
import TimePicker from "../../components/TimePicker/TimePicker";
import CheckpointService from "../../services/checkpoint.service";
import { RequestCheckpoint } from "../../types/checklist.types";
import { useTranslation } from "react-i18next";
import { changeShowLoading } from "../../components/Spinner/Spinner";
import SurgeryService from "../../services/surgery.service";

function Anesthesisvisit() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { type } = useParams();
  const [form] = Form.useForm();

  const [checkpoints, setCheckpoints] = useState(Array<RequestCheckpoint>());

  const labelColSpan = 16;
  const wrapperColSpan = 8;

  /**********USE EFFECTS**********/
  React.useEffect(() => {
    if (checkpoints.length > 0) {
      restoreForm();
    }
  }, [checkpoints]);

   React.useEffect(() => {
    getSurgeryRequestUuid();
  }, [type]);

  /**********HOOKS**********/
  const getSurgeryRequestUuid = async () => {
    let result = "";
    await changeShowLoading(
      SurgeryService.getSurgeryRequestUuid(location.state.uuid)
        .then(async(resp) => {
          if (resp && resp.surgery_request_uuid) {
            await getSurgeryRequestRevisionUuid(resp.surgery_request_uuid);
          }
        })
        .catch((err) => {
          console.log(t("ERROR"), err);
        })
    );
  };

  const getSurgeryRequestRevisionUuid = async (revisionUuid: string) => {
    let result = "";
    await changeShowLoading(
      SurgeryService.getSurgeryRequestRevisionUuid(revisionUuid)
        .then((resp) => {
          if (resp && resp.surgery_request_revision_uuid.uuid) {
            loadChecklistData(resp.surgery_request_revision_uuid.uuid);
          }
        })
        .catch((err) => {
          console.log(t("ERROR"), err);
        })
    );
  };

  const loadChecklistData = async (uuid: string) => {
    await changeShowLoading(
      CheckpointService.load(uuid)
        .then((resp) => {
            setCheckpoints(resp);
        })
        .catch((err) => {
          console.log(t("ERROR"), err);
        })
    );
  };

  const restoreForm = () => {
    let formData = {} as any;
    for (const cp of checkpoints) {
      if (cp.checkpoint.type === "timer") {
        cp.submittedAnswers = DateTime.fromISO(cp.submittedAnswers, {
          zone: "Europe/Rome", //TODO: Da scodare
        }).toString();
      }
      if (cp.submittedAnswers) {
        formData[cp.checkpoint.uuid] = cp.submittedAnswers;
      }
    }
    form.setFieldsValue({ ...formData }); // restore form data
  };

  return (
    <>
      <div className="page-container-margin">
        <div className="form-container" style={{
          maxHeight: "calc(100vh - 200px)",
          overflowY: "scroll",
        }}>
          {(checkpoints || []).map((cp: RequestCheckpoint, index: number) => {
            return (
              <Form
                name="checklist-form"
                className="checklist-form"
                form={form}
                disabled={true}
              >
                {cp.checkpoint.type === "boolean" ? (
                  <Form.Item
                    key={index}
                    name={cp.checkpoint.uuid}
                    label={cp.question}
                    className="form-container-item"
                    labelCol={{
                      span: labelColSpan,
                    }}
                    wrapperCol={{
                      span: wrapperColSpan,
                    }}
                    rules={[
                      {
                        required: cp.checkpoint.mandatory,
                        message: `${t("REQUIRED_FIELD")}`,
                      },
                    ]}
                  >
                    <Radio.Group
                      className="flex justify-end form-container-radio"
                      optionType="button"
                      buttonStyle="solid"
                      size="large"
                    >
                      <Radio value="true"> {t("YES")} </Radio>
                      <Radio value="false"> {t("NO")} </Radio>
                      <Radio value="nd"> {t("NOT_DEFINED")} </Radio>
                    </Radio.Group>
                  </Form.Item>
                ) : null}

                {cp.checkpoint.type === "text" ? (
                  <Form.Item
                    key={index}
                    name={cp.checkpoint.uuid}
                    label={cp.question}
                    className="form-container-item"
                    labelCol={{
                      span: labelColSpan,
                    }}
                    wrapperCol={{
                      span: wrapperColSpan,
                    }}
                    rules={[
                      {
                        required: cp.checkpoint.mandatory,
                        message: `${t("REQUIRED_FIELD")}`,
                      },
                    ]}
                  >
                    <Input.TextArea />
                  </Form.Item>
                ) : null}

                {cp.checkpoint.type === "dropdown" ? (
                  <Form.Item
                    key={index}
                    name={cp.checkpoint.uuid}
                    className="form-container-item"
                    label={cp.question}
                    labelCol={{
                      span: labelColSpan,
                    }}
                    wrapperCol={{
                      span: wrapperColSpan,
                    }}
                    rules={[
                      {
                        required: cp.checkpoint.mandatory,
                        message: `${t("REQUIRED_FIELD")}`,
                      },
                    ]}
                  >
                    <Select
                      className="w-full"
                      optionFilterProp="children"
                      fieldNames={{
                        label: "description",
                        value: "key",
                      }}
                      options={cp.checkpoint.answers}
                      filterOption={(input, option: any) =>
                        option.description
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    />
                  </Form.Item>
                ) : null}

                {cp.checkpoint.type === "multiple" ? (
                  <Form.Item
                    key={index}
                    name={cp.checkpoint.uuid}
                    className="form-container-item"
                    label={cp.question}
                    labelCol={{
                      span: labelColSpan,
                    }}
                    wrapperCol={{
                      span: wrapperColSpan,
                    }}
                    rules={[
                      {
                        required: cp.checkpoint.mandatory,
                        message: `${t("REQUIRED_FIELD")}`,
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      className="w-full"
                      optionFilterProp="children"
                      fieldNames={{
                        label: "description",
                        value: "key",
                      }}
                      options={cp.checkpoint.answers}
                      filterOption={(input, option: any) =>
                        option.description
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                    />
                  </Form.Item>
                ) : null}

                {cp.checkpoint.type === "timer" ? (
                  <Form.Item
                    key={index}
                    name={cp.checkpoint.uuid}
                    className="form-container-item"
                    label={cp.question}
                    labelCol={{
                      span: labelColSpan,
                    }}
                    wrapperCol={{
                      span: wrapperColSpan,
                    }}
                    rules={[
                      {
                        required: cp.checkpoint.mandatory,
                        message: `${t("REQUIRED_FIELD")}`,
                      },
                    ]}
                  >
                    <TimePicker format="HH:mm:ss" changeOnBlur />
                  </Form.Item>
                ) : null}
              </Form>
            );
          })}
        </div>
      </div>
    </>
  );
}

export default Anesthesisvisit;
